import React from "react";
import dayjs from "dayjs";
import type { NCRRecord } from "@/services/quality.service";
import type { ApprovalPrintSigner } from "@/services/approval.service";
import { GenericPaginatedDocument } from "@/components/print/GenericPaginatedDocument";
import {
  buildSimplePrintDocument,
  estimateWrappedLineCount,
  LINE_HEIGHT_PX,
} from "@/print-engine";
import "./NCRPrintView.css";

export type NCRPrintCompanyInfo = {
  name: string;
  legalName?: string;
  address: string;
  logo?: string;
};

export type NCRPrintViewProps = {
  record: NCRRecord;
  companyInfo: NCRPrintCompanyInfo;
  /** Resolved user/employee display names keyed by id (avoids raw UUIDs on print). */
  personLabels?: Record<string, string>;
  approvalSigners?: ApprovalPrintSigner[];
};

const FORM_CODE = "NTES/QSF/CNC/001 Rev.01";
const FORM_EFFECTIVE_DATE = "13-03-2024";
/** Approx. characters per full-width line at 11px on the letterhead content width. */
const CPL = 105;
const ROW_PX = 22;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function text(value?: string | null): string {
  return value == null ? "" : String(value).trim();
}

function formatDate(value?: string | null): string {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD/MM/YYYY") : "";
}

function personName(
  id: string | null | undefined,
  labels?: Record<string, string>,
): string {
  if (!id) return "";
  const label = labels?.[id];
  if (label && label.trim()) return label.trim();
  return UUID_RE.test(id) ? "" : id;
}

function textHeight(value: string, cpl = CPL): number {
  if (!value) return 0;
  return (
    value
      .split("\n")
      .reduce(
        (sum, line) => sum + estimateWrappedLineCount(line || " ", cpl),
        0,
      ) * LINE_HEIGHT_PX
  );
}

/** Section box height: heading + wrapped body + padding (body text never smaller than one line). */
function sectionHeight(body: string, minBodyLines = 1): number {
  return (
    LINE_HEIGHT_PX +
    Math.max(textHeight(body), minBodyLines * LINE_HEIGHT_PX) +
    18
  );
}

function Box({ checked }: { checked: boolean }) {
  return <span className={`ncr-box${checked ? " checked" : ""}`} />;
}

type SourceKey =
  | "external_audit"
  | "customer_complaint"
  | "product_deficiency"
  | "internal_audit"
  | "previous_cpar"
  | "other";

function sourceKey(source: NCRRecord["source"]): SourceKey {
  switch (source) {
    case "Customer Complaint":
      return "customer_complaint";
    case "Internal Audit":
      return "internal_audit";
    case "Inspection":
    case "Self-Identified":
      return "product_deficiency";
    default:
      return "other";
  }
}

/** Root-cause text: lines ending with "?" (5-Why questions) print bold like the paper form. */
function RootCauseText({ value }: { value: string }) {
  return (
    <>
      {value.split("\n").map((line, idx) =>
        line.trim().endsWith("?") ? (
          <p key={idx} className="ncr-why">
            {line}
          </p>
        ) : (
          <p key={idx} className="ncr-why-answer">
            {line || "\u00a0"}
          </p>
        ),
      )}
    </>
  );
}

/**
 * Non-Conformance Report — NTES/QSF/CNC/001 Rev.01 bordered form (matches the paper NCR / CPAR form).
 * Each form block is a keep-together final section so pages break between blocks, never inside one.
 */
export function NCRPrintView({
  record,
  companyInfo,
  personLabels = {},
  approvalSigners = [],
}: NCRPrintViewProps) {
  const capa = record.capa;
  const isObservation = Boolean(record.is_potential_nc);
  const isMajor = record.severity === "Major" || record.severity === "Critical";
  const isMinor = record.severity === "Minor";
  const src = sourceKey(record.source);

  const woNumber = record.work_order?.work_order_number || "";
  const project = record.project
    ? [record.project.project_number, record.project.project_name]
        .filter(Boolean)
        .join(" — ")
    : "";

  const requirementRefs = [
    record.drawing_no
      ? `Dwg No: ${record.drawing_no}${record.drawing_revision ? ` Rev ${record.drawing_revision}` : ""}`
      : "",
    [
      record.po_number
        ? `PO: ${record.po_number}${record.po_line ? ` / Line ${record.po_line}` : ""}`
        : "",
      record.supplier_name ? `Supplier: ${record.supplier_name}` : "",
      woNumber ? `W.O: ${woNumber}` : "",
      project ? `Project: ${project}` : "",
    ]
      .filter(Boolean)
      .join(", "),
    [
      record.part_item_no ? `Part / Item: ${record.part_item_no}` : "",
      text(record.part_description),
      record.quantity_affected != null
        ? `Qty: ${record.quantity_affected}${record.quantity_uom ? ` ${record.quantity_uom}` : ""}`
        : "",
    ]
      .filter(Boolean)
      .join(", "),
    [
      record.specification_number ? `Spec: ${record.specification_number}` : "",
      record.international_standard
        ? `Standard: ${record.international_standard}`
        : "",
      text(record.reference_documents),
    ]
      .filter(Boolean)
      .join(", "),
  ].filter(Boolean);
  const requirements = [text(record.actual_requirements), ...requirementRefs]
    .filter(Boolean)
    .join("\n");

  const description = [
    text(record.description),
    text(record.additional_information),
  ]
    .filter(Boolean)
    .join("\n");
  const sourceNotes = text(record.source_reference);
  const otherSourceLabel =
    src === "other" ? (record.source === "Other" ? "" : record.source) : "";

  const initiator =
    personName(record.identified_by, personLabels) ||
    [record.identifiedBy?.first_name, record.identifiedBy?.last_name]
      .filter(Boolean)
      .join(" ") ||
    "";
  const receivedBy = personName(record.created_by, personLabels);
  const assignedTo =
    personName(record.capa_owner_user_id, personLabels) ||
    personName(capa?.responsible_person_id, personLabels) ||
    text(capa?.responsible_person_name) ||
    personName(record.next_action_user_id, personLabels);
  const replyDue = formatDate(
    record.capa_target_date ||
      capa?.target_completion_date ||
      record.correction_target_date,
  );

  const rootCauseRows = record.root_causes?.length
    ? [...record.root_causes].sort((a, b) => a.sequence - b.sequence)
    : record.root_cause_analysis
      ? [
          {
            sequence: 1,
            root_cause_method: record.root_cause_method,
            root_cause_analysis: record.root_cause_analysis,
          },
        ]
      : [];
  const rootCauseText = rootCauseRows
    .map((r) => text(r.root_cause_analysis))
    .filter(Boolean)
    .join("\n\n");
  const rootCauseMethod = Array.from(
    new Set(
      rootCauseRows.map((r) => text(r.root_cause_method)).filter(Boolean),
    ),
  ).join(", ");

  const outcome = [
    record.disposition && record.disposition !== "Pending"
      ? `Disposition: ${record.disposition}`
      : "",
    text(record.disposition_notes),
    text(record.containment_action)
      ? `Containment: ${text(record.containment_action)}`
      : "",
  ]
    .filter(Boolean)
    .join("\n");

  const proposedAction =
    text(record.proposed_corrective_action) || text(capa?.action_plan);
  const actionTarget = formatDate(
    record.capa_target_date || capa?.target_completion_date,
  );
  const submittedBy = assignedTo;
  const submittedDate = formatDate(capa?.created_at || null);

  const actionTaken = text(record.correction_action);
  const preventiveAction =
    capa && (capa.type === "Preventive Action" || capa.type === "Both")
      ? text(capa.description) || text(capa.problem_statement)
      : "";

  const signers = [...approvalSigners].sort(
    (a, b) => a.step_number - b.step_number,
  );
  const approver = signers.length ? signers[signers.length - 1] : undefined;
  const approvalComments = signers
    .map((s) => text(s.remarks))
    .filter(Boolean)
    .join("\n");

  const followedUpBy = personName(
    record.effectiveness_verified_by,
    personLabels,
  );
  const evidence = [
    text(record.verification_evidence_notes),
    text(record.effectiveness_notes),
  ]
    .filter(Boolean)
    .join(" — ");
  const verificationDone = Boolean(
    record.effectiveness_verified_by || record.effectiveness_verified_date,
  );
  const closedBy = personName(record.closed_by, personLabels);

  const blocks: Array<{ id: string; heightPx: number; node: React.ReactNode }> =
    [
      {
        id: "ncrHead",
        heightPx: ROW_PX * 3 + 10 + sectionHeight(requirements),
        node: (
          <table className="ncr-form">
            <colgroup>
              <col style={{ width: "17%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "19%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "16%" }} />
            </colgroup>
            <tbody>
              <tr>
                <td className="ncr-accent">Report No:</td>
                <td colSpan={4}>{record.ncr_number}</td>
                <td>Date</td>
                <td>{formatDate(record.identified_date)}</td>
              </tr>
              <tr className="ncr-spacer">
                <td colSpan={7} />
              </tr>
              <tr>
                <td>
                  <Box checked={!isObservation} />
                  NCR
                </td>
                <td>
                  <Box checked={!isObservation && isMajor} />
                  Major
                </td>
                <td colSpan={2}>
                  <Box checked={!isObservation && isMinor} />
                  Minor
                </td>
                <td colSpan={3} className="ncr-label">
                  <Box checked={isObservation} />
                  OBSERVATION
                </td>
              </tr>
              <tr>
                <td colSpan={7} className="ncr-section">
                  <div className="ncr-label">Requirements:</div>
                  <div className="ncr-text">{requirements}</div>
                </td>
              </tr>
            </tbody>
          </table>
        ),
      },
      {
        id: "ncrSource",
        heightPx: ROW_PX * 4 + 8 + sectionHeight(description, 2),
        node: (
          <table className="ncr-form">
            <colgroup>
              <col style={{ width: "33%" }} />
              <col style={{ width: "33%" }} />
              <col style={{ width: "34%" }} />
            </colgroup>
            <tbody>
              <tr>
                <td
                  colSpan={3}
                  className="ncr-label"
                  style={{ borderBottom: "none", paddingBottom: 0 }}
                >
                  Source
                </td>
              </tr>
              <tr>
                <td>
                  <Box checked={src === "external_audit"} />
                  External Audit
                </td>
                <td>
                  <Box checked={src === "customer_complaint"} />
                  Customer Complaint
                </td>
                <td>
                  <Box checked={src === "product_deficiency"} />
                  Product Deficiency
                </td>
              </tr>
              <tr>
                <td>
                  <Box checked={src === "internal_audit"} />
                  Internal Audit
                </td>
                <td>
                  <Box checked={src === "previous_cpar"} />
                  Previous CPAR
                </td>
                <td>
                  <Box checked={src === "other"} />
                  Other:{otherSourceLabel ? ` ${otherSourceLabel}` : ""}
                </td>
              </tr>
              <tr>
                <td colSpan={3}>
                  <Box checked={Boolean(sourceNotes)} />
                  Notes:{sourceNotes ? ` ${sourceNotes}` : ""}
                </td>
              </tr>
              <tr>
                <td colSpan={3} className="ncr-section">
                  <div className="ncr-label">Description:</div>
                  <div className="ncr-text">{description}</div>
                </td>
              </tr>
            </tbody>
          </table>
        ),
      },
      {
        id: "ncrPeople",
        heightPx: ROW_PX * 4 + 8,
        node: (
          <table className="ncr-form">
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "24%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "16%" }} />
            </colgroup>
            <tbody>
              <tr>
                <td>Initiator:</td>
                <td>{initiator}</td>
                <td colSpan={2}>Signature:</td>
                <td className="ncr-right">Date:</td>
                <td>{formatDate(record.identified_date)}</td>
              </tr>
              <tr>
                <td colSpan={2}>
                  Received and Entered into CPAR System by:
                  <br />
                  {receivedBy}
                </td>
                <td colSpan={2}>Signature:</td>
                <td className="ncr-right">Date:</td>
                <td>{formatDate(record.created_at)}</td>
              </tr>
              <tr>
                <td colSpan={4}>Assigned to: {assignedTo}</td>
                <td className="ncr-right">Reply Due Date:</td>
                <td>{replyDue}</td>
              </tr>
            </tbody>
          </table>
        ),
      },
      {
        id: "ncrRootCause",
        heightPx:
          sectionHeight(rootCauseText, 4) +
          (rootCauseMethod ? LINE_HEIGHT_PX : 0) +
          6,
        node: (
          <table className="ncr-form">
            <tbody>
              <tr>
                <td className="ncr-section">
                  <div className="ncr-label">
                    Root Cause:{rootCauseMethod ? ` (${rootCauseMethod})` : ""}
                  </div>
                  {rootCauseText ? (
                    <RootCauseText value={rootCauseText} />
                  ) : null}
                </td>
              </tr>
            </tbody>
          </table>
        ),
      },
      {
        id: "ncrOutcome",
        heightPx: sectionHeight(outcome, 2),
        node: (
          <table className="ncr-form">
            <tbody>
              <tr>
                <td className="ncr-section">
                  <div className="ncr-label">Outcome:</div>
                  <div className="ncr-text">{outcome}</div>
                </td>
              </tr>
            </tbody>
          </table>
        ),
      },
      {
        id: "ncrProposed",
        heightPx: sectionHeight(proposedAction, 2) + ROW_PX * 2 + 4,
        node: (
          <table className="ncr-form">
            <colgroup>
              <col style={{ width: "17%" }} />
              <col style={{ width: "24%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "16%" }} />
            </colgroup>
            <tbody>
              <tr>
                <td colSpan={6} className="ncr-section">
                  <div className="ncr-label">Proposed corrective Action:</div>
                  <div className="ncr-text">{proposedAction}</div>
                </td>
              </tr>
              <tr>
                <td colSpan={4} />
                <td className="ncr-right">Action Target Date:</td>
                <td>{actionTarget}</td>
              </tr>
              <tr>
                <td>Submitted by:</td>
                <td>{submittedBy}</td>
                <td colSpan={2}>Signature:</td>
                <td className="ncr-right">Date:</td>
                <td>{submittedDate}</td>
              </tr>
            </tbody>
          </table>
        ),
      },
      {
        id: "ncrActionTaken",
        heightPx: sectionHeight(actionTaken, 2),
        node: (
          <table className="ncr-form">
            <tbody>
              <tr>
                <td className="ncr-section">
                  <div className="ncr-label">Action taken:</div>
                  <div className="ncr-text">{actionTaken}</div>
                </td>
              </tr>
            </tbody>
          </table>
        ),
      },
      {
        id: "ncrPreventive",
        heightPx: sectionHeight(preventiveAction, 2),
        node: (
          <table className="ncr-form">
            <tbody>
              <tr>
                <td className="ncr-section">
                  <div className="ncr-label">Preventive Action:</div>
                  <div className="ncr-text">{preventiveAction}</div>
                </td>
              </tr>
            </tbody>
          </table>
        ),
      },
      {
        id: "ncrClosure",
        heightPx: sectionHeight(approvalComments, 1) + ROW_PX * 4 + 24,
        node: (
          <table className="ncr-form">
            <colgroup>
              <col style={{ width: "17%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "28%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "19%" }} />
            </colgroup>
            <tbody>
              <tr>
                <td colSpan={8} className="ncr-section">
                  <span className="ncr-label">
                    Comments by Approval Authority:
                  </span>{" "}
                  <span className="ncr-text">{approvalComments}</span>
                </td>
              </tr>
              <tr>
                <td>Approved by:</td>
                <td colSpan={4}>{approver?.user.full_name || ""}</td>
                <td>
                  Signature:
                  {approver?.user.signature_image_url ? (
                    <img
                      className="ncr-sign-img"
                      src={approver.user.signature_image_url}
                      alt=""
                    />
                  ) : null}
                </td>
                <td>Date:</td>
                <td>{formatDate(approver?.acted_at)}</td>
              </tr>
              <tr>
                <td>Followed Up by:</td>
                <td colSpan={4}>{followedUpBy}</td>
                <td>Signature:</td>
                <td>Date:</td>
                <td>{formatDate(record.effectiveness_verified_date)}</td>
              </tr>
              <tr>
                <td>Effective?</td>
                <td>
                  <Box
                    checked={verificationDone && record.effectiveness_verified}
                  />
                </td>
                <td>Yes</td>
                <td>
                  <Box
                    checked={verificationDone && !record.effectiveness_verified}
                  />
                </td>
                <td>No</td>
                <td colSpan={3}>Evidence:{evidence ? ` ${evidence}` : ""}</td>
              </tr>
              <tr>
                <td>Closed Out by:</td>
                <td colSpan={4}>{closedBy}</td>
                <td>Signature:</td>
                <td>Date:</td>
                <td>{formatDate(record.closed_date)}</td>
              </tr>
            </tbody>
          </table>
        ),
      },
    ];

  const model = buildSimplePrintDocument({
    docType: "ncr",
    title: "NON-CONFORMANCE REPORT",
    subtitle: `${FORM_CODE} · Effective Date: ${FORM_EFFECTIVE_DATE}`,
    documentNumber: record.ncr_number || "NCR",
    partyName: companyInfo.legalName || companyInfo.name,
    templateId: "letter-letterhead",
    continuationLabel: `${record.ncr_number || "NCR"} | ${FORM_CODE}`,
    columns: [],
    lines: [],
    finals: blocks.map((b) => ({ id: b.id, heightPx: b.heightPx })),
    renderPayload: {
      finalBlocks: Object.fromEntries(blocks.map((b) => [b.id, b.node])),
    },
  });

  return <GenericPaginatedDocument model={model} />;
}

export default NCRPrintView;
